import React, { useEffect } from 'react';

import { Alert, Button, Checkbox, Divider, Group, Modal, Select, Stack, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconAlertSquare } from '@tabler/icons-react';
import CardInfoLine from 'components/InfoCard/CardInfoLine';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreateAthletePackageHold from 'hooks/athletePackageHold/useCreateAthletePackageHold';
import { AthletePackageRetrieve } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface ICreateAthletePackageHoldModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  isDesktop: boolean;
}

interface FormValues {
  startDate: Date | null;
  endDate: Date | null;
  reason: 'Vacation' | 'Travel' | 'Medical' | 'Financial' | 'Schedule' | 'Other' | '' | null;
  deleteUncreditedReservations: boolean;
}

const holdReasonsOptions = ['Vacation', 'Travel', 'Schedule', 'Medical', 'Financial', 'Other'];

const CreateAthletePackageHoldModal = ({
  opened,
  athletePackage,
  closeHandler,
  isDesktop,
}: ICreateAthletePackageHoldModalProps): JSX.Element => {
  const { dateFormat } = useLocalization();
  const { successNotification, errorNotification } = notificationHandler();

  const createAthletePackageHoldMutation = useCreateAthletePackageHold();

  const form = useForm<FormValues>({
    initialValues: {
      startDate: null,
      endDate: null,
      reason: null,
      deleteUncreditedReservations: true,
    },
    validate: values => ({
      startDate: values.startDate === null ? 'A start date is required' : null,
      endDate: values.endDate === null ? 'An end date is required' : null,
    }),
  });

  const handleCloseForm = (): void => {
    form.reset();
    closeHandler();
  };

  const handleSubmit = (formValues: FormValues): void => {
    const payload = {
      ...formValues,
      athletePackage: athletePackage.athletePackageId,
      startDate: dayjs(formValues.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(formValues.endDate).format('YYYY-MM-DD'),
      athletePackageHoldId: uuidv4(),
    };
    createAthletePackageHoldMutation.mutate(payload, {
      onError: error => {
        errorNotification('Pause create failed', error.response?.data.message);
        closeHandler();
      },
      onSuccess: () => {
        successNotification('Pause create success', `Athlete package successfully paused.`);
        form.reset();
        handleCloseForm();
      },
    });
  };

  useEffect(() => {
    if (form.values.startDate && form.values.endDate && form.values.startDate > form.values.endDate) {
      form.setFieldValue('endDate', dayjs(form.values.startDate).add(1, 'd').toDate());
    }
  }, [form]);

  return (
    <Modal
      opened={opened}
      onClose={handleCloseForm}
      size='lg'
      centered
      title={
        <Title order={4} pr='md'>
          Pause Package
        </Title>
      }
    >
      <Stack>
        <Group>
          <CardInfoLine label='Package Name' description={athletePackage.package.name} />
        </Group>
        <Group>
          <CardInfoLine
            label='Package Start and End Date'
            description={`${dayjs(athletePackage.startDate).format(dateFormat)} - ${dayjs(
              athletePackage.endDate,
            ).format(dateFormat)}`}
          />
        </Group>
      </Stack>
      <Divider mt='sm' />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack p='md'>
          <Select
            label='Pause Reason'
            placeholder='Select a reason'
            data={holdReasonsOptions}
            {...form.getInputProps('reason')}
          />
          <Group grow noWrap>
            <DatePickerInput
              {...form.getInputProps('startDate')}
              label='Start Date'
              clearable
              popoverProps={{ position: isDesktop ? 'left' : 'bottom', shadow: 'xl' }}
              minDate={dayjs(athletePackage.startDate).toDate()}
              maxDate={dayjs(athletePackage.endDate).toDate()}
              data-cy='holdStartDate'
              {...{ placeholder: 'Select start date' }}
            />
            <DatePickerInput
              {...form.getInputProps('endDate')}
              label='End Date'
              clearable
              popoverProps={{ position: isDesktop ? 'left' : 'bottom', shadow: 'xl' }}
              data-cy='holdEndDate'
              minDate={dayjs(form.values.startDate).toDate()}
              {...{ placeholder: 'Select end date' }}
            />
          </Group>
          <Checkbox
            {...form.getInputProps('deleteUncreditedReservations', { type: 'checkbox' })}
            label='Also remove uncredited reservations'
            description='All upcoming reservations within the pause dates are removed, including uncredited ones.'
            data-cy='holdDeleteUncreditedReservations'
          />
          <Alert variant='light' color='yellow' title='Attention' icon={<IconAlertSquare />}>
            Pausing this package will extend invoice due dates and adjust credit dispensations for this
            package and any renewals. Upcoming reservations within the pause dates are removed. The pause
            will fail if credits cannot be found for any checkins linked to this package or its renewals.
          </Alert>
        </Stack>
        <Group position='right' pt='md'>
          <Button
            variant='light'
            onClick={handleCloseForm}
            size='xs'
            radius='md'
            disabled={createAthletePackageHoldMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            size='xs'
            radius='md'
            type='submit'
            loading={createAthletePackageHoldMutation.isLoading}
            data-cy='holdsSubmit'
          >
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default CreateAthletePackageHoldModal;
